/** Typed access to VITE_ env vars. Never put secrets in VITE_ vars. */

const DEFAULT_API_BASE_URL = "http://localhost:8787";
const SURFACE_NAME = "Customer LIFF";

export type RawApiEnv = {
  VITE_API_BASE_URL?: string;
  VITE_API_MOCK?: string;
  VITE_LIFF_ID?: string;
  VITE_LIFF_MOCK?: string;
  DEV?: boolean;
};

export type ApiMode =
  | { kind: "backend"; apiBaseUrl: string }
  | { kind: "mock"; apiBaseUrl: string; reason: "explicit-local-demo" };

export class BackendApiUnavailableError extends Error {
  readonly code = "BACKEND_API_UNAVAILABLE";
  readonly recoverable = true;
  readonly requiredEnv = "VITE_API_BASE_URL";
  readonly demoOverride = "VITE_API_MOCK=1";

  constructor(surfaceName: string) {
    super(
      `${surfaceName} backend API is unavailable; set VITE_API_BASE_URL or enable explicit VITE_API_MOCK=1 for local demo mode.`,
    );
    this.name = "BackendApiUnavailableError";
  }
}

/** Resolve whether this frontend must use backend mode or explicit local mock mode. */
export function resolveApiMode(raw: RawApiEnv): ApiMode {
  const apiBaseUrl = raw.VITE_API_BASE_URL ?? DEFAULT_API_BASE_URL;
  if (raw.VITE_API_MOCK === "1") {
    return { kind: "mock", apiBaseUrl, reason: "explicit-local-demo" };
  }
  return { kind: "backend", apiBaseUrl };
}

/**
 * Resolve whether LIFF should run in mock mode (fake LINE identity, no real
 * LINE SDK). SECURITY (P0): this must be explicit opt-in only — mock mode
 * ships a hardcoded fake LINE profile (`features/liff/mock-profile.ts`), so
 * defaulting it to "on" whenever the env var is unset would let a production
 * build silently ship with a fake identity if CI ever forgot to configure the
 * var. Only the literal string "1" enables mock mode; anything else
 * (including unset) uses the real LINE LIFF SDK.
 */
export function resolveLiffMock(raw: RawApiEnv): boolean {
  return raw.VITE_LIFF_MOCK === "1";
}

/** Backend mode must fail visibly instead of silently falling back to mocks. */
export function assertBackendModeReady(mode: ApiMode, surfaceName = SURFACE_NAME): void {
  if (mode.kind === "backend" && mode.apiBaseUrl.trim().length === 0) {
    throw new BackendApiUnavailableError(surfaceName);
  }
}

export const apiMode = resolveApiMode(import.meta.env);
assertBackendModeReady(apiMode);

export const env = {
  apiBaseUrl: apiMode.apiBaseUrl,
  liffId: import.meta.env.VITE_LIFF_ID ?? "",
  /** Mock LIFF ONLY when explicitly enabled (VITE_LIFF_MOCK=1). See `resolveLiffMock`. */
  liffMock: resolveLiffMock(import.meta.env),
  apiMock: apiMode.kind === "mock",
  apiMode: apiMode.kind,
  isDev: import.meta.env.DEV,
} as const;
